
import {buildDefaults} from "./SettingsSchema.js";

// Single localStorage key, versioned JSON blob (not one key per setting) -
// see task requirement: one consolidated, versionable storage structure
// rather than scattered keys.
const STORAGE_KEY = "potree.digitization.settings";
const STORAGE_VERSION = 1;

function getByPath(obj, path){
	return path.split(".").reduce((node, key) => (node == null ? undefined : node[key]), obj);
}

function setByPath(obj, path, value){
	const parts = path.split(".");
	let node = obj;
	for(let i = 0; i < parts.length - 1; i++){
		if(typeof node[parts[i]] !== "object" || node[parts[i]] === null) node[parts[i]] = {};
		node = node[parts[i]];
	}
	node[parts[parts.length - 1]] = value;
}

// Merges `stored` on top of `defaults`, recursively, keeping only keys that
// already exist in `defaults` and whose stored value has the same type as
// the default. This single rule is what makes the store tolerant of every
// case the task calls out:
//  - a setting added *after* the user's last save simply isn't in `stored`
//    yet, so the default is kept;
//  - a setting removed/renamed since the user's last save exists in
//    `stored` but not in `defaults`, so it's silently dropped;
//  - a corrupted or wrong-typed stored value (e.g. a boolean setting saved
//    as the string "true" by some future bug, or hand-edited localStorage)
//    fails the typeof check, so the default wins instead of propagating
//    garbage into the app.
function mergeWithDefaults(defaults, stored){
	const result = {};
	for(const groupId of Object.keys(defaults)){
		result[groupId] = {...defaults[groupId]};
		const storedGroup = stored && typeof stored[groupId] === "object" && stored[groupId] !== null ? stored[groupId] : null;
		if(!storedGroup) continue;

		for(const key of Object.keys(defaults[groupId])){
			if(key in storedGroup && typeof storedGroup[key] === typeof defaults[groupId][key]){
				result[groupId][key] = storedGroup[key];
			}
		}
	}
	return result;
}

// Real localStorage when available; a plain in-memory Map otherwise (no
// persistence across reloads, but the store stays fully functional) - keeps
// DigitizationSettingsStore usable in any environment (a plain Node test
// runner has no `localStorage` global at all) without special-casing tests,
// and means a browser that blocks/throws on storage access degrades to
// "settings don't persist" rather than "Digitalisering doesn't start".
function createDefaultStorageBackend(){
	if(typeof localStorage !== "undefined"){
		return localStorage;
	}
	const memory = new Map();
	return {
		getItem: (key) => (memory.has(key) ? memory.get(key) : null),
		setItem: (key, value) => { memory.set(key, String(value)); },
		removeItem: (key) => { memory.delete(key); },
	};
}

// Single central place to define defaults (SettingsSchema.js), get/set a
// setting, reset one setting/one group/everything, and persist to storage -
// see digitization/index.js for the exported singleton (`settings`). Tools
// and UI code should always go through that singleton; nothing else should
// call localStorage.getItem/setItem directly for Digitalisering settings.
export class DigitizationSettingsStore {
	constructor(storageBackend = createDefaultStorageBackend()){
		this._storage = storageBackend;
		this._defaults = buildDefaults();
		this._state = mergeWithDefaults(this._defaults, this._loadFromStorage());
		this._listeners = new Set();
	}

	// dot-path, e.g. settings.get("vectorEditing.maxSagitta")
	get(key){
		const value = getByPath(this._state, key);
		return value !== undefined ? value : getByPath(this._defaults, key);
	}

	// e.g. settings.set("vectorEditing.maxSagitta", 0.05)
	set(key, value){
		setByPath(this._state, key, value);
		this._saveToStorage();
		this._notify(key, value);
	}

	// reset(): every setting back to default.
	// reset("vectorEditing"): only that group's settings.
	// reset("vectorEditing.maxSagitta"): only that one setting.
	reset(scope){
		if(!scope){
			this._state = JSON.parse(JSON.stringify(this._defaults));
		}else if(scope.includes(".")){
			setByPath(this._state, scope, getByPath(this._defaults, scope));
		}else if(this._defaults[scope]){
			this._state[scope] = {...this._defaults[scope]};
		}
		this._saveToStorage();
		this._notify(scope || "*", undefined);
	}

	// returns an unsubscribe function
	onChange(fn){
		this._listeners.add(fn);
		return () => this._listeners.delete(fn);
	}

	_notify(key, value){
		for(const fn of this._listeners) fn(key, value);
	}

	_loadFromStorage(){
		try {
			const raw = this._storage.getItem(STORAGE_KEY);
			if(!raw) return null;
			const parsed = JSON.parse(raw);
			if(!parsed || typeof parsed !== "object" || typeof parsed.values !== "object" || parsed.values === null) return null;
			return parsed.values;
		} catch(e) {
			// malformed JSON, or storage access itself threw - fall back to
			// defaults rather than letting this reach the caller
			return null;
		}
	}

	_saveToStorage(){
		try {
			this._storage.setItem(STORAGE_KEY, JSON.stringify({version: STORAGE_VERSION, values: this._state}));
		} catch(e) {
			// quota exceeded, storage disabled (private browsing), etc. -
			// settings just won't persist across reloads; never throw from here
		}
	}
}

export const settings = new DigitizationSettingsStore();
